import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { People } from '@local/client-contracts';
import { EventsService } from '@shared/services';
import { KeyboardService } from '@shared/services/keyboard.service';
import { getModifiers, isEnterKey } from '@local/ts-infra';
import { HierarchyComponent } from 'src/app/bar/components/hierarchy/components/hierarchy/hierarchy.component';
import { HierarchyItem } from 'src/app/bar/components/hierarchy/models/hierarchyItem';
import { ComponentFocused } from 'src/app/bar/models/component-focused.model';
import { PreviewMode } from '../../../model/preview-mode';
export const DEFAULT_PERSON_AVATAR = '/assets/bar/people/people-avatar.svg';

@Component({
  selector: 'preview-hierarchy',
  templateUrl: './preview-hierarchy.component.html',
  styleUrls: ['./preview-hierarchy.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PreviewHierarchyComponent implements OnInit, OnDestroy {
  private readonly COMP_INDEX = 3;
  private keyHandlerId: string;
  private _details: People.PersonDetails;
  @Input() set details(value: People.PersonDetails) {
    this._details = value;
    this.generateViewItems();
  }
  get details(): People.PersonDetails {
    return this._details;
  }
  @Input() set componentFocused({ compIndex, direction }: ComponentFocused) {
    if (compIndex === this.COMP_INDEX) {
      if (this.keyHandlerId) return;
      this.keyHandlerId = this.keyboardService.registerKeyHandler((keys, event) => {
        this.handleKeys(keys, event);
      }, 6);
      this.markedIndex = direction === 'first' ? 0 : this.elementsList.length - 1;
      this.setElementsList();
      return;
    }
    this.unregisterKeyHandler();
  }
  @Input() smallWidthPreview = true;
  @Input() previewPopupLarge: boolean;
  @Input() size: PreviewMode;
  @Output() selectedItemToPopUp: EventEmitter<HierarchyItem> = new EventEmitter<HierarchyItem>();
  @Output() componentFocusedExit: EventEmitter<string> = new EventEmitter<string>();
  @Output() readyForFocus: EventEmitter<number> = new EventEmitter<number>();
  @ViewChild('hierarchy') hierarchyRef: HierarchyComponent;
  currentItem: HierarchyItem;
  subordinateItems: HierarchyItem[];
  superiorItems: HierarchyItem[];
  elementsList: ElementRef['nativeElement'][] = [];
  markedIndex: number;
  selectedElement: string;
  openSuperiorItems = false;
  private trigger: 'mouse_click' | 'keyboard' = 'mouse_click';
  readonly defaultPersonAvatar = DEFAULT_PERSON_AVATAR;

  constructor(private eventsService: EventsService, private cdr: ChangeDetectorRef, private keyboardService: KeyboardService) {}
  ngOnInit(): void {
    if (this.superiorItems?.length || this.subordinateItems?.length) {
      this.readyForFocus.emit(this.COMP_INDEX);
    }
  }

  ngOnDestroy(): void {
    this.unregisterKeyHandler();
  }

  unregisterKeyHandler() {
    if (!this.keyHandlerId) return;
    this.keyboardService.unregisterKeyHandler(this.keyHandlerId);
    this.markedIndex = -1;
    this.keyHandlerId = null;
  }

  private generateCurrentItem(): void {
    this.currentItem = {
      id: this.details?.externalId,
      countAll: this.details?.employees?.total,
      countDirect: this.details?.employees?.direct,
      icon: this.details?.avatar || this.defaultPersonAvatar,
      fallbackIcon: this.defaultPersonAvatar,
      subTitle: this.details?.jobTitle,
      title: this.details?.displayName,
    };
  }

  private generateSuperiorItems(): void {
    this.superiorItems = this.details?.managers.map((manager) => {
      return {
        id: manager?.externalId,
        countAll: manager?.employeeCount,
        countDirect: manager?.directEmployeeCount,
        icon: manager?.avatar || this.defaultPersonAvatar,
        fallbackIcon: this.defaultPersonAvatar,
        subTitle: manager?.jobTitle,
        title: manager?.displayName,
      };
    });
  }

  private generateSubordinateItems(): void {
    this.subordinateItems = this.details?.employees?.employees.map((employee) => {
      return {
        id: employee?.externalId,
        icon: employee?.avatar || this.defaultPersonAvatar,
        fallbackIcon: this.defaultPersonAvatar,
        title: employee?.displayName,
      };
    });
  }

  private generateViewItems(): void {
    this.generateSuperiorItems();
    this.generateSubordinateItems();
    this.generateCurrentItem();
  }

  onSelectedItemToPopUp(item: HierarchyItem): void {
    item.trigger = this.trigger;
    this.trigger = 'mouse_click';
    this.selectedItemToPopUp.emit(item);
  }

  openEmployeeList(): void {
    this.eventsService.event('people_view.open_dropdown');
  }

  setElementsList() {
    this.elementsList = [];
    if (this.hierarchyRef?.superiorItemsRef?.length)
      this.elementsList.push(...(this.hierarchyRef?.superiorItemsRef?.map((el) => el.nativeElement) || []));
    if (!this.openSuperiorItems && this.hierarchyRef?.countRef) this.elementsList.push(this.hierarchyRef?.countRef.nativeElement);
    if (this.openSuperiorItems) this.elementsList.push(...(this.hierarchyRef?.hiddenSuperiorsRef?.map((el) => el.nativeElement) || []));
    if (this.hierarchyRef?.superiorAboveRef) this.elementsList.push(this.hierarchyRef?.superiorAboveRef.nativeElement);
    if (this.hierarchyRef?.superiorBelowRef) this.elementsList.push(this.hierarchyRef?.superiorBelowRef.nativeElement);
    if ([2, 3].includes(this.subordinateItems?.length))
      this.elementsList.push(...(this.hierarchyRef?.avatarBelowItems?.map((e) => e.nativeElement) || []));
    if (this.hierarchyRef?.openHierarchyRef) this.elementsList.push(this.hierarchyRef?.openHierarchyRef.nativeElement);
    this.cdr.markForCheck();
  }

  handleKeys(keys, event) {
    const modifiers = getModifiers(keys);
    const key = keys[0];
    if (key === 'tab') {
      event.stopPropagation();
      event.preventDefault();
      if (modifiers[0] === 'shift') {
        if (this.markedIndex === 0) {
          this.componentFocusedExit.emit('prev');
        }
        this.markedIndex--;
        this.setElementsList();
        return;
      }
      if (this.markedIndex === this.elementsList.length - 1) {
        this.componentFocusedExit.emit('next');
      } else this.markedIndex++;
    }
    if (isEnterKey(key)) {
      event.stopPropagation();
      event.preventDefault();
      this.trigger = 'keyboard';
      if (this.elementsList[this.markedIndex]?.id?.includes('hierarchy')) {
        this.selectedElement = this.elementsList[this.markedIndex].id;
      } else {
        this.elementsList[this.markedIndex].dispatchEvent(new MouseEvent('click'));
      }
    }
    this.setElementsList();
  }
}
